'use client'

import React, { useId, useState } from 'react'
import type { TabsBlock as TabsBlockType, Media } from '@/payload-types'
import './tabs.scss'

export const TabsBlock: React.FC<TabsBlockType> = ({
  color = 'primary',
  tabsWidth = 'col-lg-12',
  image,
  layout = 'tabsImage',
  noImageFilter = false,
  filterOpacity = '0.65',
  items,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0) // Premier onglet actif par défaut
  // Stable unique ID for this tabs instance (SSR-safe, no hydration mismatch)
  const reactId = useId()

  if (!items || items.length === 0) {
    return null
  }

  // Convert color to PascalCase for CSS class
  const colorValue = color || 'primary'
  const colorClass = `color${colorValue.charAt(0).toUpperCase() + colorValue.slice(1)}`

  const tabsId = `tabs-${reactId.replace(/[^a-zA-Z0-9-]/g, '')}`

  const handleTabClick = (index: number) => {
    setActiveTab(index)
  }

  // Check if we have an image
  const hasImage = tabsWidth !== 'col-lg-12'

  // Calculate image column class
  const getImageColumnClass = () => {
    if (tabsWidth === 'col-lg-6') return 'col-lg-6'
    if (tabsWidth === 'col-lg-9') return 'col-lg-3'
    return ''
  }

  const imageColumnClass = getImageColumnClass()

  // Get image URL
  const imageUrl = typeof image === 'object' && image !== null ? (image as Media).url : ''

  // Filter classes
  const filterClass = noImageFilter ? '' : colorClass
  const opacityClass = noImageFilter ? '' : `opacity-${(filterOpacity || '0.65').replace('.', '-')}`

  // Tabs content
  const tabsContent = (
    <div className={`col-12 ${tabsWidth}`}>
      <div className={`tabs ${colorClass}`}>
        <ul className="nav nav-tabs nav-justified" id={tabsId} role="tablist">
          {items.map((item, index) => {
            const isActive = activeTab === index
            const tabId = `${tabsId}-tab-${index}`
            const panelId = `${tabsId}-panel-${index}`

            return (
              <li key={index} className="nav-item" role="presentation">
                <button
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  id={tabId}
                  type="button"
                  role="tab"
                  aria-controls={panelId}
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => handleTabClick(index)}
                >
                  {item.title}
                </button>
              </li>
            )
          })}
        </ul>
        <div className="tab-content" id={`${tabsId}-content`}>
          {items.map((item, index) => {
            const isActive = activeTab === index
            const tabId = `${tabsId}-tab-${index}`
            const panelId = `${tabsId}-panel-${index}`

            return (
              <div
                key={index}
                className={`tab-pane fade ${isActive ? 'show active' : ''}`}
                id={panelId}
                role="tabpanel"
                aria-labelledby={tabId}
                tabIndex={0}
              >
                {item.content}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )

  // Image content
  const imageContent = hasImage ? (
    <div className={`col-12 ${imageColumnClass}`}>
      <div
        className={`tabsImage ${filterClass} ${opacityClass}`}
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
    </div>
  ) : null

  // If no image, just render tabs
  if (!hasImage) {
    return tabsContent
  }

  // If image, render in layout order
  return (
    <div className="tabsContainer">
      <div className="row g-0">
        {layout === 'tabsImage' ? (
          <>
            {tabsContent}
            {imageContent}
          </>
        ) : (
          <>
            {imageContent}
            {tabsContent}
          </>
        )}
      </div>
    </div>
  )
}
